/// <amd-module name="Release/Commands/DeleteFolderCommandService" />
import * as React from "react";

import { IVssService, VssService, Services } from "VSS/Platform/Context";
import { IVssLayoutManager, IWrappedComponentProperties, WrappedComponent } from "VSS/Platform/Layout";
import { localeFormat } from "VSS/Core/Util/String";

import { IMenuItem } from "VSSUI/Menu";

import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { IReleaseService } from "Release/Client/Service/Release";
import { hasRmPermissions, ReleaseManagementSecurityPermissions } from "Release/Helpers/Permission";
import { publishClickEvent, publishSuccessEvent, publishFailedEvent } from "Release/Helpers/Telemetry";
import { IDeleteFolderDialogProps } from "./DialogTypes";
import * as Resources from "Release/Commands/Resources";

export const deleteFolderCommand: (context: IReleaseDefinitionPivotCommandContext, deleteCallback?: (folderPath: string) => void) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext, deleteCallback?: (folderPath: string) => void) => {
    if (context.folderPath !== "\\" && hasRmPermissions(context.pageContext, context.folderPath, context.pipelineId, ReleaseManagementSecurityPermissions.DeleteReleaseDefinition)) {
        return {
            id: "delete-release-folder-command",
            text: Resources.DeleteFolderCommand,
            important: false,
            iconProps: {
                iconName: "Delete"
            },
            onActivate: () => {
                context.pageContext.getService<IDeleteFolderCommandService>("IDeleteFolderCommandService").deleteFolder(context, deleteCallback);
            }
        };
    }
};

export interface IDeleteFolderCommandService extends IVssService {
    deleteFolder(context: IReleaseDefinitionPivotCommandContext, deleteCallback?: (folderPath: string) => void): void;
}

class DeleteFolderCommandService extends VssService implements IDeleteFolderCommandService {
    public deleteFolder(context: IReleaseDefinitionPivotCommandContext, deleteCallback?: (folderPath: string) => void): void {
        publishClickEvent(this.pageContext, deleteFolderFeature, context.telemetrySource);
        if (context.folderPath) {
            const layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout((onDismiss) => {
                const props: IDeleteFolderDialogProps & IWrappedComponentProperties = {
                    wrappedType: "rel-delete-folder-dialog",
                    dependencies: ["ms.vss-releaseManagement-web.release-command-dialogs"],
                    folderPath: context.folderPath,
                    onOkClick: () => {
                        if (context.folderPath) {
                            return this.pageContext.getService<IReleaseService>("IReleaseService")
                                .deleteFolder(context.folderPath)
                                .then(() => {
                                    publishSuccessEvent(this.pageContext, deleteFolderFeature, context.telemetrySource);
                                    if (deleteCallback) {
                                        deleteCallback(context.folderPath);
                                    }

                                    context.successCallback(localeFormat(Resources.FolderDeletedMessage, context.folderPath));
                                }, (error: any) => {
                                    publishFailedEvent(this.pageContext, deleteFolderFeature, context.telemetrySource);
                                    context.errorCallback((error && error.message) || error);
                                });
                        }
                    },
                    onClose: onDismiss
                };

                return React.createElement(WrappedComponent, props);
            });
        }
    }
}

const deleteFolderFeature: string = "Command_DeleteFolder";

Services.add("IDeleteFolderCommandService", { serviceFactory: DeleteFolderCommandService });
